import React, { useState, useCallback, useEffect } from 'react';

import { createStyles, Stack, Group, Text, useMantineTheme } from '@mantine/core';
import { Eventcalendar, MbscCalendarEvent, MbscEventClickEvent } from '@mobiscroll/react';
import dayjs from 'dayjs';
import useProgramCalendar from 'hooks/program/useProgramCalendar';
import useDebounce from 'hooks/useDebounce';
import { ProgramRetrieve, KiloClassType } from 'types';

import { FilterTerms, ProgramCalendarView } from './types';

export interface CalendarTileData {
  id: string;
  programId: string;
  start: Date;
  programName: string;
  totalPrograms: number;
  workoutNotes: string | null;
  kiloClassType: KiloClassType;
  createdAt: string;
  updatedAt: string;
  gym: string;
  programDate: string;
  isProgrammed: boolean;
}

interface IProgramCalendarMonthViewProps {
  programCalendarNavigationButtons: () => JSX.Element;
  searchFilters: FilterTerms;
  setView: React.Dispatch<React.SetStateAction<ProgramCalendarView>>;
  setDate: React.Dispatch<React.SetStateAction<string>>;
  setProgram: React.Dispatch<React.SetStateAction<ProgramRetrieve | undefined>>;
  calendarMaxDate?: Date;
  handleEditProgram: (programId: string) => void;
}

const useStyles = createStyles(theme => ({
  calendar: {
    fontFamily: `${theme.fontFamily} !important`,
    borderRadius: theme.radius.md,
  },
}));

const ProgramCalendarMonthView = ({
  programCalendarNavigationButtons,
  searchFilters,
  setView,
  setDate,
  setProgram,
  handleEditProgram,
  calendarMaxDate,
}: IProgramCalendarMonthViewProps): JSX.Element => {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const [start, setStart] = useState<string>();
  const [end, setEnd] = useState<string>();
  const debouncedStart = useDebounce(start, 200);
  const debouncedEnd = useDebounce(end, 200);
  const [calendarEvents, setCalendarEvents] = useState<MbscCalendarEvent[]>([]);
  const [maxTotalPrograms, setMaxTotalPrograms] = useState(1);

  const {
    data: programData,
    isError: programIsError,
    isSuccess: programIsSuccess,
  } = useProgramCalendar({
    start: debouncedStart,
    end: debouncedEnd,
    ...searchFilters,
  });

  const onPageLoading = (event: {
    firstDay: string | number | Date | dayjs.Dayjs | null | undefined;
    lastDay: string | number | Date | dayjs.Dayjs | null | undefined;
  }): void => {
    const s = dayjs(event.firstDay).format('YYYY-MM-DD');
    const e = dayjs(event.lastDay).format('YYYY-MM-DD');
    setStart(s);
    setEnd(e);
  };

  const handleProgramClick = useCallback(
    (e: MbscEventClickEvent): void => {
      const programFromEvent = {
        programId: e.event.programId,
        kiloClassType: e.event.kiloClassType,
        workoutNotes: e.event.workoutNotes,
        date: e.event.programDate,
        createdAt: e.event.createdAt,
        updatedAt: e.event.updatedAt,
        gym: e.event.gym,
        isProgrammed: e.event.isProgrammed,
      };
      handleEditProgram(e.event.id?.toString() || '');
      setProgram(programFromEvent);
    },
    [handleEditProgram, setProgram],
  );

  const handleCellClick = useCallback(
    (data: any): void => {
      setDate(dayjs(data.date).format('YYYY-MM-DD'));
      setView('agenda');
    },
    [setDate, setView],
  );

  const renderLabel = useCallback(
    (data: any): JSX.Element => {
      const { totalPrograms, kiloClassType, programName, isProgrammed } = data.original;
      return (
        <Stack p={0} spacing='xs' data-cy='programMonthItem'>
          <Group
            sx={{
              gap: 0,
              backgroundColor: isProgrammed ? kiloClassType.color : 'grey',
              borderRadius: `calc(${theme.spacing.lg} / 5)`,
            }}
            p={`calc(${theme.spacing.xs} /4 )`}
          >
            <Text size='md' fw='bold' pl='xs'>
              {programName}
            </Text>
          </Group>
          <Group sx={{ gap: `calc(${theme.spacing.xs} / 1)`, p: 0 }}>
            {totalPrograms > 1 && (
              <Text size='md' pl='xs'>
                {totalPrograms - 1} more
              </Text>
            )}
          </Group>
        </Stack>
      );
    },
    [theme],
  );

  useEffect(() => {
    const getTotalPrograms = (
      programs: ProgramRetrieve[],
    ): { calendar: CalendarTileData[]; maxPrograms: number } => {
      let maxPrograms = 0;
      const programCalendar = programs.reduce((acc: Record<string, CalendarTileData>, program) => {
        const { date: curDate, programId } = program;
        const tempProgram = { ...acc };
        if (tempProgram[curDate]) {
          tempProgram[curDate].totalPrograms += 1;
        } else {
          tempProgram[curDate] = {
            start: new Date(`${curDate}T00:00`),
            totalPrograms: 1,
            id: programId,
            programId: program.programId,
            programDate: program.date,
            workoutNotes: program.workoutNotes,
            kiloClassType: program.kiloClassType,
            programName: program.kiloClassType.name,
            createdAt: program.createdAt,
            updatedAt: program.updatedAt,
            gym: program.gym,
            isProgrammed: program.isProgrammed,
          };
        }

        if (tempProgram[curDate].totalPrograms > maxPrograms) {
          maxPrograms = tempProgram[curDate].totalPrograms;
        }

        return { ...acc, ...tempProgram };
      }, {});

      return { calendar: Object.values(programCalendar), maxPrograms };
    };

    if (programIsSuccess && programData) {
      const { calendar, maxPrograms } = getTotalPrograms(programData.data);
      setMaxTotalPrograms(maxPrograms);
      setCalendarEvents(
        calendar.map((event: CalendarTileData): MbscCalendarEvent => {
          return {
            ...event,
            backgroundColor: `rgb(0, 158, 227, ${event.totalPrograms / maxPrograms})`,
          };
        }),
      );
    }
  }, [theme, programIsSuccess, programData, maxTotalPrograms]);

  if (programIsError) {
    return <>Something went wrong</>;
  }

  return (
    <>
      <Eventcalendar
        renderHeader={programCalendarNavigationButtons}
        theme='ios'
        themeVariant='light'
        className={classes.calendar}
        data={calendarEvents}
        renderLabel={renderLabel}
        onPageLoading={onPageLoading}
        onEventClick={handleProgramClick}
        onCellClick={handleCellClick}
        max={calendarMaxDate}
        view={{ calendar: { labels: true } }}
      />
    </>
  );
};

export default ProgramCalendarMonthView;
